// File: /src/components/PixQRCode.js

import React from 'react';
import QRCode from 'react-qr-code';
import { FaShareAlt, FaCopy } from 'react-icons/fa';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { toast } from 'react-toastify';
import '../styles/CustomToastify.css';

const formatCurrency = (value) => {
	return new Intl.NumberFormat('pt-BR', {
		style: 'currency',
		currency: 'BRL',
	}).format(value);
};

const PixQRCode = ({ invoice, pixKey, client, user }) => {
	// Validação detalhada
	if (!invoice) {
		console.error('❌ Fatura não fornecida');
		return <div style={{ padding: '20px', textAlign: 'center' }}>Erro: Fatura não encontrada</div>;
	}
	
	if (!pixKey) {
		console.error('❌ Chave PIX não fornecida');
		return (
			<div style={{ padding: '20px', textAlign: 'center' }}>
				<p>⚠️ Chave PIX não configurada.</p>
				<p style={{ fontSize: '14px', color: '#666' }}>
					Por favor, configure sua chave PIX nas configurações da conta.
				</p>
			</div>
		);
	}
	
	if (!client) {
		console.error('❌ Cliente não fornecido');
		return <div style={{ padding: '20px', textAlign: 'center' }}>Erro: Cliente não encontrado</div>;
	}
	
	if (!user) {
		console.error('❌ Usuário não fornecido');
		return <div style={{ padding: '20px', textAlign: 'center' }}>Carregando dados do usuário...</div>;
	}

	// Função para calcular CRC16
	function crc16CCITT(str) {
		let crc = 0xffff;
		for (let c = 0; c < str.length; c++) {
			crc ^= str.charCodeAt(c) << 8;
			for (let i = 0; i < 8; i++) {
				if (crc & 0x8000) {
					crc = (crc << 1) ^ 0x1021;
				} else {
					crc = crc << 1;
				}
			}
		}
		return (crc & 0xffff).toString(16).toUpperCase().padStart(4, '0');
	}

	// Função para adicionar o campo com seu tamanho
	const addLength = (id, content) => {
		const len = content.length.toString().padStart(2, '0');
		return `${id}${len}${content}`;
	};

	// Formata o valor para PIX
	const formatValue = (value) => {
		return Number(value).toFixed(2);
	};

	// Monta o payload do PIX
	const createPixPayload = () => {
		try {
			const payloadFormat = '01';
			const gui = 'BR.GOV.BCB.PIX';
			const merchantAccountInfo =
				addLength('00', gui) + addLength('01', pixKey);
			const merchantCategCode = '0000';
			const transactionCurrency = '986';
			const transactionAmount = formatValue(invoice.value);
			const countryCode = 'BR';
			const merchantName =
				user?.name
					?.normalize('NFD')
					.replace(/[\u0300-\u036f]/g, '')
					.replace(/[^a-zA-Z0-9\s]/g, '')
					.trim()
					.substring(0, 25) || 'LOCATIME';

			const merchantCity =
				user?.city
					?.normalize('NFD')
					.replace(/[\u0300-\u036f]/g, '')
					.replace(/[^a-zA-Z0-9\s]/g, '')
					.trim()
					.substring(0, 15) || 'SAO PAULO';
			const txid = `INV${invoice.id}`;

			let payload = '';
			payload += addLength('00', payloadFormat);
			payload += addLength('26', merchantAccountInfo);
			payload += addLength('52', merchantCategCode);
			payload += addLength('53', transactionCurrency);
			payload += addLength('54', transactionAmount);
			payload += addLength('58', countryCode);
			payload += addLength('59', merchantName);
			payload += addLength('60', merchantCity);
			payload += addLength('62', addLength('05', txid));
			payload += '6304';
			const crc = crc16CCITT(payload);
			payload += crc;

			return payload;
		} catch (error) {
			console.error('Erro ao gerar payload PIX:', error);
			return '';
		}
	};

	// Função para copiar o payload para a área de transferência
	const handleCopyPayload = async () => {
		const payload = createPixPayload();
		try {
			await navigator.clipboard.writeText(payload);
			toast.success('Código PIX copiado!', {
				position: 'top-right',
				autoClose: 3000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: true,
				draggable: true,
				progress: undefined,
			});
		} catch (err) {
			console.error('Erro ao copiar código PIX:', err);
			toast.error('Erro ao copiar!', {
				position: 'top-right',
				autoClose: 3000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: true,
				draggable: true,
				progress: undefined,
			});
		}
	};

	// Função para gerar o PDF
	const generatePDF = async () => {
		const element = document.getElementById('qr-code-container');
		const canvas = await html2canvas(element, {
			scale: 2,
			useCORS: true,
			logging: false,
			backgroundColor: null,
			imageTimeout: 0,
		});

		const imgData = canvas.toDataURL('image/png', 0.7);

		const pdf = new jsPDF({
			orientation: 'portrait',
			unit: 'mm',
			format: 'a4',
			compress: true,
		});

		const pageWidth = 210;
		const pageHeight = 297;
		const margin = 20;
		const contentWidth = pageWidth - 2 * margin;
		const yStart = 60;

		pdf.setFillColor(37, 99, 235);
		pdf.rect(0, 0, pageWidth, 40, 'F');
		pdf.setTextColor(255, 255, 255);
		pdf.setFont('helvetica', 'bold');
		pdf.setFontSize(24);
		pdf.text('Pagamento PIX', margin, 25);
		const currentDate = new Date().toLocaleDateString('pt-BR');
		pdf.setFontSize(12);
		pdf.text(currentDate, pageWidth - margin - 20, 25);
		pdf.setTextColor(0, 0, 0);
		pdf.setFont('helvetica', 'normal');
		pdf.setFontSize(12);
		pdf.text('Informações do Pagamento:', margin, yStart);
		pdf.setDrawColor(230, 230, 230);
		pdf.setFillColor(250, 250, 250);
		pdf.roundedRect(margin, yStart + 5, contentWidth, 40, 3, 3, 'FD');
		pdf.setFontSize(11);
		pdf.text(
			`Cliente: ${client?.name || 'LOCATIME'}`,
			margin + 6,
			yStart + 16
		);
		pdf.text(
			`Valor: ${formatCurrency(invoice.value)}`,
			margin + 6,
			yStart + 26
		);
		pdf.text(`Chave PIX: ${pixKey}`, margin + 6, yStart + 31);
		pdf.text(
			`Favorecido: ${user?.name || 'LOCATIME'}`,
			margin + 6,
			yStart + 36
		);

		const desiredWidth = pageWidth / 3.5;
		const desiredHeight = desiredWidth;

		// Posiciona centralizado
		const xPos = (pageWidth - desiredWidth) / 2;
		const yPos = yStart + 55;

		// Adiciona a imagem mantendo o formato quadrado
		pdf.addImage(
			imgData,
			'PNG',
			xPos,
			yPos,
			desiredWidth,
			desiredHeight,
			'',
			'FAST'
		);

		const yInstructions = yPos + desiredHeight + 20;
		pdf.setFont('helvetica', 'bold');
		pdf.setFontSize(12);
		pdf.text('Como pagar:', margin, yInstructions);
		pdf.setFont('helvetica', 'normal');
		pdf.setFontSize(11);
		const instructions = [
			'1. Abra o aplicativo do seu banco',
			'2. Escolha a opção de pagamento via PIX',
			'3. Escaneie o QR Code acima ou insira a chave pix e valor',
			'4. Confirme as informações e finalize o pagamento',
		];
		instructions.forEach((instruction, index) => {
			pdf.text(instruction, margin, yInstructions + 10 + index * 7);
		});
		pdf.setFontSize(9);
		pdf.setTextColor(128, 128, 128);
		const footer = `Documento gerado por ${
			user?.name || 'LOCATIME'
		} em ${currentDate}`;
		pdf.text(footer, margin, pageHeight - 20);

		return pdf;
	};

	const shareViaNative = async () => {
		if (navigator.share) {
			try {
				const pdf = await generatePDF();
				const pdfBlob = pdf.output('blob');
				const file = new File(
					[pdfBlob],
					`pix-qrcode-${invoice.id}.pdf`,
					{
						type: 'application/pdf',
					}
				);

				await navigator.share({
					title: 'Pagamento PIX',
					text: `Pagamento PIX - ${formatCurrency(invoice.value)}`,
					files: [file],
				});
			} catch (error) {
				console.error('Erro ao compartilhar:', error);
				const pdf = await generatePDF();
				pdf.save(`pix-qrcode-${invoice.id}.pdf`);
			}
		} else {
			const pdf = await generatePDF();
			pdf.save(`pix-qrcode-${invoice.id}.pdf`);
		}
	};

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				gap: '20px',
				width: '100%',
				padding: '20px',
				textAlign: 'center',
			}}
		>
			<div
				id="qr-code-container"
				style={{
					background: 'white',
					padding: '16px',
					textAlign: 'center',
					width: '100%',
					maxWidth: '300px',
					margin: '0 auto',
					borderRadius: '12px',
					boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
				}}
			>
				<QRCode
					value={createPixPayload()}
					size={256}
					level="M"
					style={{
						width: '100%',
						height: 'auto',
						maxWidth: '196px',
						display: 'block',
						margin: '0 auto',
					}}
				/>
				<p
					style={{
						marginTop: '16px',
						marginBottom: '8px',
						color: '#4a5568',
					}}
				>
					Chave PIX: {pixKey}
				</p>
				<p
					style={{
						fontSize: '1em',
						fontWeight: 'bold',
						color: '#2d3748',
					}}
				>
					Valor: {formatCurrency(invoice.value)}
				</p>
			</div>

			<div
				style={{
					width: '100%',
					maxWidth: '300px',
					display: 'flex',
					flexDirection: 'column',
					gap: '10px',
				}}
			>
				<button
					onClick={handleCopyPayload}
					className="pix-button"
					title="Pix Copia e Cola"
				>
					<FaCopy /> PIX Copia e Cola
				</button>

				<button
					onClick={shareViaNative}
					className="share-button"
					title="Compartilhar PDF"
				>
					<FaShareAlt /> Compartilhar
				</button>
			</div>
		</div>
	);
};

export default PixQRCode;
